import { Injectable } from '@angular/core';
import {
    HttpInterceptor,
    HttpHandler,
    HttpRequest,
    HttpEvent,
    HttpErrorResponse,
    HttpContextToken,
} from '@angular/common/http';

/**
 * Set on a request's context to suppress the global error modal for that request
 * (the spinner is still stopped and the error still re-thrown for local handling).
 * Use for endpoints whose errors are expected/benign, e.g. a 404 "no receipt yet".
 */
export const SKIP_ERROR_MODAL = new HttpContextToken<boolean>(() => false);
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { BsModalService } from 'ngx-bootstrap/modal';
import { InvalidModalComponent } from '../modules/shared/modals/invalid-modal/invalid-modal.component';

/**
 * Global HTTP error handler. Without this, any backend error (e.g. 422 validation,
 * 500) leaves the global ngx-ui-loader spinner running forever and shows nothing to
 * the user (many create/save flows subscribe without an error callback).
 *
 * On any HTTP error it: stops the spinner, shows a message, and re-throws so any
 * component-level error handling still runs.
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    constructor(private ngxLoader: NgxUiLoaderService, private bsModalService: BsModalService) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(req).pipe(
            catchError((err: HttpErrorResponse) => {
                this.ngxLoader.stopAll();
                if (!req.context.get(SKIP_ERROR_MODAL)) {
                    this.bsModalService.show(InvalidModalComponent, {
                        initialState: { message: this.buildMessage(err) },
                    });
                }
                return throwError(() => err);
            })
        );
    }

    private buildMessage(err: HttpErrorResponse): string {
        const fallback = 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';

        // Network / server unreachable
        if (err.status === 0) {
            return 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาตรวจสอบอินเทอร์เน็ตแล้วลองใหม่';
        }

        const body = err.error as
            | { message?: string; data?: { errors?: unknown; type?: string } }
            | null
            | undefined;
        const errors = body?.data?.errors;

        // 422 validation: errors is { field: [messages] } -> surface the field messages
        if (err.status === 422 && errors && typeof errors === 'object' && !Array.isArray(errors)) {
            const msgs = Object.values(errors as Record<string, unknown>)
                .reduce<string[]>((acc, v) => acc.concat(Array.isArray(v) ? (v as string[]) : [String(v)]), [])
                .filter((m) => typeof m === 'string' && m.length);
            if (msgs.length) {
                return msgs.join('\n');
            }
        }

        // Business/known error carrying a human-readable message (skip the generic 500 title)
        if (typeof body?.message === 'string' && body.message && body.message !== 'Internal Server Error') {
            return body.message;
        }

        // Never surface raw SQL / stack traces from 500s -> generic message
        return fallback;
    }
}
